import { apiClient } from './api'

export interface Provider {
  id: string
  userId: string
  userName: string
  userEmail: string
  businessName: string
  code: string
  businessDescription?: string
  companyRegistrationNumber?: string
  vatRegistrationNumber?: string
  website?: string
  status: ProviderStatus
  type: ProviderType
  isVerified: boolean
  verifiedAt?: string
  verifiedByName?: string
  rejectionReason?: string
  commissionRate: number
  isActive: boolean
  businessDayFrom?: string
  businessDayTo?: string
  businessTimeFrom?: string
  businessTimeTo?: string
  businessAddress?: string
  businessCity?: string
  businessState?: string
  businessCountry?: string
  businessPostalCode?: string
  specialNotes?: string
  averageRating: number
  totalReviews: number
  totalCompletedAppointments: number
  lastActiveAt?: string
  createdAt: string
  updatedAt: string
  // Multi-region: ISO 3166-1 alpha-2 (e.g. "GB", "PK") + ISO 4217 currency
  // (e.g. "GBP", "PKR"). Drives address label ("County" vs "Province"), tax
  // label (VAT vs GST), country default, currency display. Nullable — null
  // is treated as "GB" by getAddressFormat / RegionDefaults.
  regionCode?: string | null
  currency?: string | null
  // Receipt printer preferences (apply to both shop orders and service bookings)
  autoPrintReceipts: boolean
  autoPrintOnOrderConfirmation: boolean
  // Provider-level feature toggles (admin-controlled)
  shelfLifeEnabled: boolean
  zavInvoiceEnabled: boolean
  // BNPL (Klarna, Clearpay) opt-in for service bookings — provider-managed
  bnplEnabled: boolean
  bnplMinOrderAmount?: number | null
  // Invoice settings (provider-level)
  bankName?: string
  bankAccountName?: string
  bankSortCode?: string
  bankAccountNumber?: string
  invoicePrefix?: string
  defaultPaymentTerms?: string
  defaultVatRate?: number
  invoiceTermsAndConditions?: string
  specializations: ProviderSpecialization[]
  documents: ProviderDocument[]
  paymentMethods: ProviderPaymentMethod[]
  servicesCount: number
  locationsCount: number
}

export interface ProviderList {
  id: string
  userId: string
  userName: string
  userEmail: string
  businessName: string
  code: string
  businessDescription?: string
  status: ProviderStatus
  type: ProviderType
  isVerified: boolean
  isActive: boolean
  averageRating: number
  totalReviews: number
  servicesCount: number
  shopsCount: number
  createdAt: string
  lastActiveAt?: string
  matchedOn?: string
  hasWholesaleShop?: boolean
}

export interface ProviderSpecialization {
  id: string
  specializationId: string
  specializationName: string
  yearsOfExperience: number
  certificationNumber?: string
  certificationDate?: string
  expiryDate?: string
  issuingAuthority?: string
  isVerified: boolean
}

export interface ProviderDocument {
  id: string
  type: DocumentType
  name: string
  description: string
  fileUrl: string
  fileName?: string
  fileSize?: number
  status: DocumentStatus
  expiryDate?: string
  rejectionReason?: string
  reviewedAt?: string
  reviewedByName?: string
  createdAt: string
}

export interface ProviderPaymentMethod {
  id: string
  type: PaymentMethodType
  maskedAccountNumber: string
  accountHolderName?: string
  bankName?: string
  sortCode?: string
  iban?: string
  swiftCode?: string
  isDefault: boolean
  isActive: boolean
  isVerified: boolean
  verifiedAt?: string
}

export interface Specialization {
  id: string
  name: string
  description: string
  icon?: string
  color?: string
  category?: string
  isActive: boolean
  requiresCertification: boolean
  providersCount: number
}

export interface ProviderStats {
  totalProviders: number
  activeProviders: number
  pendingProviders: number
  verifiedProviders: number
  averageRating: number
  totalServices: number
  totalCompletedAppointments: number
}

export enum ProviderStatus {
  Pending = 0,
  UnderReview = 1,
  Approved = 2,
  Rejected = 3,
  Suspended = 4
}

export enum ProviderType {
  Individual = 0,
  Business = 1,
  Organization = 2
}

export enum DocumentType {
  CompanyRegistration = 0,
  TaxDocument = 1,
  Insurance = 2,
  Certification = 3,
  Identification = 4,
  Contract = 5,
  Other = 6
}

export enum DocumentStatus {
  Pending = 0,
  UnderReview = 1,
  Approved = 2,
  Rejected = 3,
  Expired = 4
}

export enum PaymentMethodType {
  BankAccount = 0,
  CreditCard = 1,
  PayPal = 2,
  Stripe = 3,
  Wallet = 4
}

export interface ProviderSearchRequest {
  searchTerm?: string
  status?: ProviderStatus
  type?: ProviderType
  isVerified?: boolean
  isActive?: boolean
  specializationId?: string
  minRating?: number
  wholesaleOnly?: boolean
  page?: number
  pageSize?: number
  sortBy?: string
  sortDirection?: string
}

export interface ProviderSearchResponse {
  providers: ProviderList[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  specializations: Specialization[]
}

export interface CreateProviderRequest {
  businessName: string
  businessDescription?: string
  type: ProviderType
  companyRegistrationNumber?: string
  vatRegistrationNumber?: string
  website?: string
  businessDayFrom?: string
  businessDayTo?: string
  businessTimeFrom?: string
  businessTimeTo?: string
  businessAddress?: string
  businessCity?: string
  businessState?: string
  businessCountry?: string
  businessPostalCode?: string
  specialNotes?: string
  specializationIds: string[]
}

export interface UpdateProviderRequest {
  businessName: string
  businessDescription?: string
  companyRegistrationNumber?: string
  vatRegistrationNumber?: string
  website?: string
  type?: ProviderType
  businessDayFrom?: string
  businessDayTo?: string
  businessTimeFrom?: string
  businessTimeTo?: string
  businessAddress?: string
  businessCity?: string
  businessState?: string
  businessCountry?: string
  businessPostalCode?: string
  specialNotes?: string
  profileImageUrl?: string
  // Receipt printer preferences — omit to leave unchanged
  autoPrintReceipts?: boolean
  autoPrintOnOrderConfirmation?: boolean
}

export interface UpdateProviderStatusRequest {
  status: ProviderStatus
  rejectionReason?: string
}

export const providerService = {
  // Provider CRUD operations
  async getProviders(params: ProviderSearchRequest = {}): Promise<ProviderSearchResponse> {
    const response = await apiClient.get<ProviderSearchResponse>('/providers', { params })
    return response
  },

  async getProvider(id: string): Promise<Provider> {
    const response = await apiClient.get<Provider>(`/providers/${id}`)
    return response
  },

  async getMyProvider(): Promise<Provider> {
    console.log('API: Calling GET /providers/my-profile')
    try {
      const providerData = await apiClient.get<Provider>('/providers/my-profile')
      console.log('API: Provider data received:', providerData)
      console.log('API: Provider data type:', typeof providerData)
      
      if (providerData) {
        console.log('API: Successfully loaded Provider:', {
          id: providerData.id,
          businessName: providerData.businessName,
          status: providerData.status
        })
        return providerData
      } else {
        throw new Error('Provider profile not found - no Provider entity exists for this user')
      }
    } catch (error: any) {
      console.error('API: Error in getMyProvider:', error)
      console.error('API: Error response:', error.response?.data)
      console.error('API: Error status:', error.response?.status)
      throw error
    }
  },

  async createProvider(data: CreateProviderRequest): Promise<Provider> {
    const response = await apiClient.post('/providers', data)
    return response
  },

  async updateProvider(id: string, data: UpdateProviderRequest): Promise<Provider> {
    const response = await apiClient.put(`/providers/${id}`, data)
    return response
  },

  async updateProviderStatus(id: string, data: UpdateProviderStatusRequest): Promise<Provider> {
    const response = await apiClient.put(`/providers/${id}/status`, data)
    return response
  },

  /**
   * Admin-only: toggle provider-level feature modules (Zav Shelf, Zav Invoice).
   */
  async updateProviderFeatures(
    id: string,
    data: { shelfLifeEnabled?: boolean; zavInvoiceEnabled?: boolean }
  ): Promise<Provider> {
    const response = await apiClient.put(`/providers/${id}/admin/features`, data)
    return response
  },

  /**
   * Provider-scoped: toggle Buy Now Pay Later (Klarna, Clearpay) on service
   * bookings + set the minimum booking amount at which BNPL appears at
   * checkout. Callable by the provider or by admin.
   */
  async updateProviderBnplSettings(
    id: string,
    data: { bnplEnabled: boolean; bnplMinOrderAmount?: number | null }
  ): Promise<Provider> {
    const response = await apiClient.put(`/providers/${id}/bnpl-settings`, data)
    return response
  },

  /**
   * Update provider-level invoice settings (bank details, prefix, VAT defaults, T&Cs).
   * Callable by the provider or by admin.
   */
  async updateProviderInvoiceSettings(
    id: string,
    data: {
      bankName?: string
      bankAccountName?: string
      bankSortCode?: string
      bankAccountNumber?: string
      invoicePrefix?: string
      defaultPaymentTerms?: string
      defaultVatRate?: number
      invoiceTermsAndConditions?: string
      autoCarryOver?: boolean
    }
  ): Promise<Provider> {
    const response = await apiClient.put(`/providers/${id}/invoice-settings`, data)
    return response
  },

  // Top rated providers
  async getTopRatedProviders(count: number = 10): Promise<ProviderList[]> {
    const response = await apiClient.get('/providers/top-rated', { params: { count } })
    return response
  },

  // Provider statistics
  async getProviderStats(): Promise<ProviderStats> {
    const response = await apiClient.get<ProviderStats>('/providers/stats')
    return response
  },

  // Specializations
  async getSpecializations(isActive: boolean = true): Promise<Specialization[]> {
    try {
      const data = await apiClient.get('/specializations', {
        params: { isActive }
      })
      return data || []
    } catch (error) {
      console.error('Error fetching specializations:', error)
      throw error
    }
  },

  async getAllSpecializations(isActive?: boolean): Promise<Specialization[]> {
    try {
      const params: any = {}
      if (isActive !== undefined) {
        params.isActive = isActive
      }
      const data = await apiClient.get('/specializations', { params })
      return data || []
    } catch (error) {
      console.error('Error fetching all specializations:', error)
      throw error
    }
  },

  // Admin Specialization CRUD operations
  async createSpecialization(data: {
    name: string
    description: string
    icon?: string
    color?: string
    requiresCertification: boolean
  }): Promise<Specialization> {
    const response = await apiClient.post('/specializations', data)
    return response
  },

  async updateSpecialization(id: string, data: {
    name: string
    description: string
    icon?: string
    color?: string
    requiresCertification: boolean
  }): Promise<Specialization> {
    const response = await apiClient.put(`/specializations/${id}`, data)
    return response
  },

  async deleteSpecialization(id: string): Promise<void> {
    await apiClient.delete(`/specializations/${id}`)
  },

  // Helper functions
  getProviderStatusLabel(status: ProviderStatus): string {
    switch (status) {
      case ProviderStatus.Pending:
        return 'Pending'
      case ProviderStatus.UnderReview:
        return 'Under Review'
      case ProviderStatus.Approved:
        return 'Approved'
      case ProviderStatus.Rejected:
        return 'Rejected'
      case ProviderStatus.Suspended:
        return 'Suspended'
      default:
        return 'Unknown'
    }
  },

  getProviderTypeLabel(type: ProviderType): string {
    switch (type) {
      case ProviderType.Individual:
        return 'Individual'
      case ProviderType.Business:
        return 'Business'
      case ProviderType.Organization:
        return 'Organization'
      default:
        return 'Unknown'
    }
  },

  getDocumentTypeLabel(type: DocumentType): string {
    switch (type) {
      case DocumentType.CompanyRegistration:
        return 'Company Registration'
      case DocumentType.TaxDocument:
        return 'Tax Document'
      case DocumentType.Insurance:
        return 'Insurance'
      case DocumentType.Certification:
        return 'Certification'
      case DocumentType.Identification:
        return 'Identification'
      case DocumentType.Contract:
        return 'Contract'
      case DocumentType.Other:
        return 'Other'
      default:
        return 'Unknown'
    }
  },

  getDocumentStatusLabel(status: DocumentStatus): string {
    switch (status) {
      case DocumentStatus.Pending:
        return 'Pending'
      case DocumentStatus.UnderReview:
        return 'Under Review'
      case DocumentStatus.Approved:
        return 'Approved'
      case DocumentStatus.Rejected:
        return 'Rejected'
      case DocumentStatus.Expired:
        return 'Expired'
      default:
        return 'Unknown'
    }
  },

  getPaymentMethodTypeLabel(type: PaymentMethodType): string {
    switch (type) {
      case PaymentMethodType.BankAccount:
        return 'Bank Account'
      case PaymentMethodType.CreditCard:
        return 'Credit Card'
      case PaymentMethodType.PayPal:
        return 'PayPal'
      case PaymentMethodType.Stripe:
        return 'Stripe'
      case PaymentMethodType.Wallet:
        return 'Wallet'
      default:
        return 'Unknown'
    }
  },

  getStatusColor(status: ProviderStatus): string {
    switch (status) {
      case ProviderStatus.Pending:
        return 'orange'
      case ProviderStatus.UnderReview:
        return 'blue'
      case ProviderStatus.Approved:
        return 'green'
      case ProviderStatus.Rejected:
        return 'red'
      case ProviderStatus.Suspended:
        return 'red'
      default:
        return 'gray'
    }
  },

  getRatingStars(rating: number): string {
    const fullStars = Math.floor(rating)
    const hasHalfStar = rating % 1 >= 0.5
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0)
    
    return '★'.repeat(fullStars) + 
           (hasHalfStar ? '☆' : '') + 
           '☆'.repeat(emptyStars)
  },

  // Document operations
  async getProviderDocuments(providerId: string, status?: DocumentStatus, type?: DocumentType): Promise<ProviderDocument[]> {
    const params = new URLSearchParams()
    if (status !== undefined) params.append('status', status.toString())
    if (type !== undefined) params.append('type', type.toString())
    
    const url = `/providers/${providerId}/documents?${params}`
    console.log('Getting documents from URL:', url)
    
    try {
      const response = await apiClient.get(url)
      console.log('Get documents response:', response)
      console.log('Response type:', typeof response)
      console.log('Is response array?:', Array.isArray(response))
      console.log('Documents count:', Array.isArray(response) ? response.length : 'Not an array')
      
      // The apiClient.get() already returns response.data, so response is the actual data
      return Array.isArray(response) ? response : []
    } catch (error: any) {
      console.error('Error getting provider documents:', error)
      console.error('Error response:', error.response?.data)
      console.error('Error status:', error.response?.status)
      
      // 404 is expected when provider has no documents
      if (error.response?.status === 404) {
        return []
      }

      throw error
    }
  },

  async getProviderDocument(providerId: string, documentId: string): Promise<ProviderDocument> {
    const response = await apiClient.get(`/providers/${providerId}/documents/${documentId}`)
    return response
  },

  async uploadProviderDocument(
    providerId: string,
    type: DocumentType,
    name: string,
    description: string,
    documentFile: File,
    expiryDate?: string
  ): Promise<ProviderDocument> {
    console.log('uploadProviderDocument called with:', {
      providerId,
      type,
      name,
      description,
      fileName: documentFile.name,
      fileSize: documentFile.size,
      expiryDate
    })

    const formData = new FormData()
    formData.append('documentFile', documentFile)
    formData.append('type', type.toString())
    formData.append('name', name)
    formData.append('description', description)
    if (expiryDate) formData.append('expiryDate', expiryDate)

    console.log('Making API call to:', `/providers/${providerId}/documents`)
    
    try {
      const response = await apiClient.post(`/providers/${providerId}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      console.log('Raw API response:', response)
      console.log('Response type:', typeof response)
      console.log('Response structure:', response)
      
      // The apiClient.post() already returns response.data, so response is the actual data
      return response
    } catch (error: any) {
      console.error('API error in uploadProviderDocument:', error)
      console.error('Error response:', error.response?.data)
      console.error('Error status:', error.response?.status)
      console.error('Error config:', error.config)
      throw error
    }
  },

  async updateProviderDocument(
    providerId: string, 
    documentId: string, 
    name: string,
    description: string,
    expiryDate?: string
  ): Promise<ProviderDocument> {
    const formData = new FormData()
    formData.append('name', name)
    formData.append('description', description)
    if (expiryDate) formData.append('expiryDate', expiryDate)

    const response = await apiClient.put(`/providers/${providerId}/documents/${documentId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response
  },

  async deleteProviderDocument(providerId: string, documentId: string): Promise<void> {
    await apiClient.delete(`/providers/${providerId}/documents/${documentId}`)
  },

  async downloadProviderDocument(providerId: string, documentId: string): Promise<void> {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:50000/api'}/providers/${providerId}/documents/${documentId}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken') || ''}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const contentType = response.headers.get('Content-Type')

      // Check if response is JSON (pre-signed URL) or binary (streamed file)
      if (contentType?.includes('application/json')) {
        // Pre-signed URL response - redirect to S3 for direct download
        const data = await response.json()
        if (data.downloadUrl) {
          // Open the pre-signed URL in a new tab/window for download
          // This triggers the browser's native download behavior
          const link = document.createElement('a')
          link.href = data.downloadUrl
          link.download = data.fileName || `document-${documentId}`
          link.target = '_blank'
          link.rel = 'noopener noreferrer'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          return
        }
      }

      // Fallback: Handle streamed binary response (local storage)
      const contentDisposition = response.headers.get('Content-Disposition')
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `document-${documentId}.pdf`

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename || `document-${documentId}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error: any) {
      console.error('Download error:', error)
      throw error
    }
  },

  // Provider Specialization operations
  async getProviderSpecializations(providerId: string): Promise<ProviderSpecialization[]> {
    try {
      const response = await apiClient.get(`/providers/${providerId}/specializations`)
      return Array.isArray(response) ? response : []
    } catch (error: any) {
      console.error('Error getting provider specializations:', error)
      if (error.response?.status === 404) {
        return []
      }
      throw error
    }
  },

  async addProviderSpecialization(
    providerId: string,
    specializationId: string,
    yearsOfExperience: number,
    certificationNumber?: string,
    issuingAuthority?: string,
    certificationDate?: string,
    expiryDate?: string
  ): Promise<ProviderSpecialization> {
    const data = {
      specializationId: specializationId, // Backend expects GUID string
      yearsOfExperience,
      certificationNumber: certificationNumber || null,
      issuingAuthority: issuingAuthority || null,
      certificationDate: certificationDate ? new Date(certificationDate).toISOString() : null,
      expiryDate: expiryDate ? new Date(expiryDate).toISOString() : null
    }

    console.log('Sending addProviderSpecialization request:', data)
    const response = await apiClient.post(`/providers/${providerId}/specializations`, data)
    return response
  },

  async updateProviderSpecialization(
    providerId: string,
    specializationId: string,
    yearsOfExperience: number,
    certificationNumber?: string,
    issuingAuthority?: string,
    certificationDate?: string,
    expiryDate?: string
  ): Promise<ProviderSpecialization> {
    const data = {
      yearsOfExperience,
      certificationNumber: certificationNumber || null,
      issuingAuthority: issuingAuthority || null,
      certificationDate: certificationDate ? new Date(certificationDate).toISOString() : null,
      expiryDate: expiryDate ? new Date(expiryDate).toISOString() : null
    }

    console.log('Sending updateProviderSpecialization request:', data)
    const response = await apiClient.put(`/providers/${providerId}/specializations/${specializationId}`, data)
    return response
  },

  async removeProviderSpecialization(providerId: string, specializationId: string): Promise<void> {
    await apiClient.delete(`/providers/${providerId}/specializations/${specializationId}`)
  },

  // Provider Payment Method operations
  async addPaymentMethod(
    providerId: string,
    type: PaymentMethodType,
    accountNumber: string,
    accountHolderName?: string,
    bankName?: string,
    sortCode?: string,
    swiftCode?: string,
    iban?: string,
    isDefault: boolean = false
  ): Promise<ProviderPaymentMethod> {
    const data = {
      type,
      accountNumber,
      accountHolderName: accountHolderName || null,
      bankName: bankName || null,
      sortCode: sortCode || null,
      swiftCode: swiftCode || null,
      iban: iban || null,
      isDefault
    }

    console.log('Sending addPaymentMethod request:', data)
    const response = await apiClient.post(`/providers/${providerId}/payment-methods`, data)
    return response
  },

  async updatePaymentMethod(
    providerId: string,
    paymentMethodId: string,
    accountNumber: string,
    accountHolderName?: string,
    bankName?: string,
    sortCode?: string,
    swiftCode?: string,
    iban?: string,
    isDefault: boolean = false
  ): Promise<ProviderPaymentMethod> {
    const data = {
      accountNumber,
      accountHolderName: accountHolderName || null,
      bankName: bankName || null,
      sortCode: sortCode || null,
      swiftCode: swiftCode || null,
      iban: iban || null,
      isDefault
    }

    console.log('Sending updatePaymentMethod request:', data)
    const response = await apiClient.put(`/providers/${providerId}/payment-methods/${paymentMethodId}`, data)
    return response
  },

  async deletePaymentMethod(providerId: string, paymentMethodId: string): Promise<void> {
    await apiClient.delete(`/providers/${providerId}/payment-methods/${paymentMethodId}`)
  },

  async setDefaultPaymentMethod(providerId: string, paymentMethodId: string): Promise<ProviderPaymentMethod> {
    const response = await apiClient.put(`/providers/${providerId}/payment-methods/${paymentMethodId}/set-default`, {})
    return response
  }
}